/**
 * Short codes of people (HA, HAL): one fixed, unique code per person that stands for the person in
 * documents (day text of a Stundennachweis), plans and lists.
 *
 * Why a stored code and not an abbreviation computed per document: a computed abbreviation ("E.Y."; "E. Yılmaz"
 * as soon as two people of a group share the initials) changes with the composition of the group, so the same
 * person appears differently in two documents. The team already works with fixed codes. The code is a field of
 * the person record, unique in the company, suggested from the name and changeable by the office.
 */

/** Name particles that do not count as initials (von, van, de, ...). */
const PARTICLES = new Set(['von', 'van', 'vom', 'zu', 'zur', 'de', 'der', 'den', 'del', 'di', 'da', 'dos', 'ten', 'ter', 'bin', 'ibn', 'el', 'al']);
const VOWELS = new Set('AEIOUÄÖÜÂÎÛÉÈÊ'.split('').concat(['İ', 'I']));

function parts(name: string): string[] {
  return name
    .replace(/[,;]/g, ' ')
    .split(/[\s-]+/)
    .map((p) => p.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean);
}

function significant(name: string): string[] {
  const all = parts(name);
  const sig = all.filter((p, i) => i === 0 || i === all.length - 1 || !PARTICLES.has(p.toLowerCase()));
  return sig.length ? sig : all;
}

const up = (s: string) => s.toUpperCase();

/** Initials of first and last name ("Hasan Aksoy" -> "HA"); middle names and particles are ignored. One name part gives its first two letters. */
export function initialsOf(name: string): string {
  const sig = significant(name);
  if (!sig.length) return '';
  if (sig.length === 1) return up(sig[0].slice(0, 2));
  return up(sig[0][0] + sig[sig.length - 1][0]);
}

/** Upper case, no spaces or dots. */
export function normalizeShortCode(code: string | null | undefined): string {
  return up((code ?? '').replace(/[\s.]/g, ''));
}

/** 2 to 6 letters or digits. */
export function isValidShortCode(code: string): boolean {
  return /^[\p{L}\p{N}]{2,6}$/u.test(code);
}

/**
 * Suggestion for a new person: the initials ("Hasan Aksoy" -> HA). If they are taken, the next consonant of the first name
 * (then of the last name) is added ("Halil Arslan" -> HAL), finally a number (HA2).
 * `taken`: codes in use (case does not matter).
 */
export function suggestShortCode(name: string, taken: Iterable<string>): string {
  const used = new Set<string>();
  for (const c of taken) used.add(normalizeShortCode(c));
  const sig = significant(name);
  const base = initialsOf(name);
  if (!base) return '';
  if (!used.has(base)) return base;
  const first = up(sig[0] ?? '');
  const last = up(sig.length > 1 ? sig[sig.length - 1] : '');
  const letters = [...first.slice(1), ...last.slice(1)].filter((c) => !VOWELS.has(c));
  for (const c of letters) {
    const cand = base + c;
    if (!used.has(cand)) return cand;
  }
  for (let n = 2; n < 100; n++) {
    const cand = base + n;
    if (!used.has(cand)) return cand;
  }
  return base;
}

/**
 * Codes for people that have none, in the given order (the oldest account first gets the plain initials).
 * Returns person id -> new code; stored codes are kept.
 */
export function assignMissingCodes(people: { id: string; name: string; code: string | null }[]): Map<string, string> {
  const taken = new Set(people.filter((p) => p.code).map((p) => normalizeShortCode(p.code)));
  const out = new Map<string, string>();
  for (const p of people) {
    if (p.code) continue;
    const c = suggestShortCode(p.name, taken);
    if (!c) continue;
    taken.add(c);
    out.set(p.id, c);
  }
  return out;
}

/** Codes that appear more than once (case-insensitive): code -> person ids. */
export function duplicateCodes(people: { id: string; code: string | null }[]): Map<string, string[]> {
  const m = new Map<string, string[]>();
  for (const p of people) {
    const c = normalizeShortCode(p.code);
    if (!c) continue;
    m.set(c, [...(m.get(c) ?? []), p.id]);
  }
  for (const [k, v] of [...m]) if (v.length < 2) m.delete(k);
  return m;
}
